import nanoid from "nanoid";
import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { GetDataSource_syncarto_app_data_source } from "../../../../../../__generated__/types";
import Widget from "../../../../components/Elements/Widget";
import { DataSourceRasterLayer, Field } from "../../../../types/types";
import { fetchSsurgoSoil } from "../../../../utils/ssrugoSoils";
import { useLayerStoreSoilsView } from "../../providers/LayerProviderSoilsView";
import SoilInterpretationsForm from "../SoilInterpretationsForm";
import SoilInterpTable from "../SoilInterpTable";
import useMapLayers from '../../../../hooks/useMapLayers';

interface SoilInfoProps {
    field: Field
}

type QueryParams = {
    keyId: number;
    name: string;
}

type SoilData = {
    tableData: any,
    interpName: string
};

const SoilInfoWidgetSoilsView = ({ field }: SoilInfoProps) => {
    const { addLayer, removeLayer } = useLayerStoreSoilsView();

    const [data, setData] = useState<SoilData | null>(null);
    const [layerName, setLayerName] = useState<string>("");
    const [layerState, setLayerState] = useState<any>(null);
    const [showError, setShowError] = useState<boolean>(false);
    const [queryParams, setQueryParams] = useState<QueryParams | null>(null);
    const [visible, setVisible] = useState<boolean>(true);

    useEffect(() => {
        setVisible(false);
    }, []);

    useEffect(() => {
        removeLayer(layerName);
        setData(null);

        if(queryParams) {
            onSubmit(queryParams.keyId, queryParams.name);
        }
        
    }, [field]);

    useEffect(() => {
        if (visible){
            if (layerState){
                addLayer(layerState);
            }
        }else{
            removeLayer(layerName);
        }
    }, [visible]);

    const onSubmit = async (keyId:number, name: string) => {
        try {
            setQueryParams({
                keyId,
                name
            });

            removeLayer(layerName);
            setData(null);
            setShowError(false);

            const result =  await fetchSsurgoSoil(keyId, field.geom);

            setData({
                tableData: result.tableData,
                interpName: name
            });

            const layer = createWmsLayer(field, result.overlayData);
            setLayerState(layer)
            setLayerName(layer.id);
            if (visible){
                addLayer(layer);
            }
        }
        catch(e) {
            setShowError(true);
        }
    }

    const createWmsLayer = (field: Field, overlayData: any) => {
        const dataSourceLayer: DataSourceRasterLayer = {
            name: `soil_field_${field.id}`,
            id: `soil_field_${field.id}`,
            uuid: nanoid(8),
            min_zoom: 10,
            max_zoom: 24,
            is_visible: true,
            type: 'overlay',
            order: 1,
            source: {} as GetDataSource_syncarto_app_data_source,
            tiles: [
                `https://sdmdataaccess.sc.egov.usda.gov/Spatial/SDM.wms?bbox={bbox-epsg-3857}&format=image/png&service=WMS&version=1.1.1&request=GetMap&srs=EPSG:3857&width=256&height=256&TRANSPARENT=true&LAYERS=mapunitpolythematic%2Caoilabeled&INTERPRESULTID=${overlayData.interpId}`
            ]
        };

        return dataSourceLayer;
    }

    const clearLayer = () => {
        removeLayer(layerName);
        setData(null);
        setLayerState(null);
        setQueryParams(null);
    }

    return (
        <Widget title="" renderActions={() => (
        <>        
            <Form.Check 
                type="checkbox"
                label="NRCS Soils"
                checked={visible}
                onChange={(e:any) => {
                    const nextState = !visible;
                    setVisible(nextState);
                }}
            />
            {
                data && <Button size="sm" variant="light" onClick={() => clearLayer()}>
                    <span className="text-xs">CLEAR</span>
                </Button>
            }
        </>)}>
            {data && 
                <div className="mb-4">
                    <SoilInterpTable legend={data.tableData.Table} data={data.tableData.Table1} title={data.interpName}></SoilInterpTable>
                </div>
            }

            <div>
                <SoilInterpretationsForm 
                    onSubmit={onSubmit} 
                    field={field}
                />
                { showError && 
                <div className="text-xs text-danger mt-2">
                    Current selected interpretation not available for this area.
                </div>
                }
            </div>
        </Widget>
    )
}

export default SoilInfoWidgetSoilsView;